import React, {
    createContext,
    useContext
} from 'react';

import { useInfiniteScroll } from '$hooks/ajax-listing';

/** @type {React.Context<import('$types/components/partials/base.d.ts').AjaxListingContext>} */
const AjaxListingContext = createContext({
    items: [],
    loading: false,
    page: null,
    setPage: () => { },
    status: null,
    totalItems: null,
    totalPages: null
});
AjaxListingContext.displayName = 'AjaxListingContext';

/* ========================================================================= */
/* Components */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/base.d.ts').AjaxListingNextButton}
 */
const AjaxListingNextButton = (props) => {
    const { page, setPage, totalPages } = useContext(AjaxListingContext);

    return (
        <button
            aria-label={props.label || 'Next page'}
            className={props.className}
            disabled={totalPages <= page}
            onClick={setPage.bind(this, page + 1)}
            type='button'>
            {props.children}
        </button>
    );
};

/**
 * @type {import('$types/components/partials/base.d.ts').AjaxListingPreviousButton}
 */
const AjaxListingPreviousButton = (props) => {
    const { page, setPage } = useContext(AjaxListingContext);

    return (
        <button
            aria-label='Previous page'
            className={props.className}
            disabled={page <= 1}
            onClick={setPage.bind(this, page - 1)}
            type='button'>
            {props.children}
        </button>
    );
};

/**
 * @type {import('$types/components/partials/base.d.ts').AjaxListingPageButton}
 */
const AjaxListingPageButton = (props) => {
    const { page, setPage } = useContext(AjaxListingContext);

    return (
        <button
            aria-label={`page ${props.page}`}
            className={props.className}
            disabled={page === props.page}
            onClick={setPage.bind(this, props.page)}
            type='button'>
            {props.children}
        </button>
    );
};

/**
 * @type {import('$types/components/partials/base.d.ts').InfinitePagination}
 */
const InfinitePagination = (props) => {
    const {
        loading,
        page,
        setPage,
        totalPages
    } = useContext(AjaxListingContext);

    const hasNextPage = totalPages > page;

    const infiniteRef = useInfiniteScroll({
        loading: loading,
        hasNextPage: hasNextPage,
        onLoad: () => {
            setPage(page + 1);
        }
    });

    if (hasNextPage || loading) {
        return (
            <div
                aria-label='Load more'
                className={props.className}
                ref={infiniteRef}
                tabIndex={0}>
                {props.children}
            </div>
        );
    };

    return null;
};

/* ========================================================================= */
/* Exports */
/* ========================================================================= */

export {
    AjaxListingContext,
    AjaxListingNextButton,
    AjaxListingPageButton,
    AjaxListingPreviousButton,
    InfinitePagination
};
